import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Filter, SortAsc, SortDesc, FileDown, Search, RefreshCw, Award, BarChart3, Users} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Main component
const MeritListAnalytics = () => {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('meritlistanalytics');
  const isRTL = i18n.language === 'ar';
  
  // State management
  const [loading, setLoading] = useState(true);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [meritList, setMeritList] = useState([]);
  const [filteredMeritList, setFilteredMeritList] = useState([]);
  
  // Selected options
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState("all");
  
  // Filter and view options
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'class_position', direction: 'asc' });
  const [viewMode, setViewMode] = useState('table'); // table, subject-analysis, class-analysis
  const [showOnlyTopTen, setShowOnlyTopTen] = useState(false);
  
  // Performance metrics
  const [performanceMetrics, setPerformanceMetrics] = useState({
    classAverage: 0,
    highestScore: 0,
    lowestScore: 0,
    aboveAverage: 0,
    belowAverage: 0,
  });

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch academic years with terms
        const yearResponse = await api.get('/api/v1/academic-years/list-with-terms');
        setAcademicYears(yearResponse.data);
        
        // Set current year as default
        const currentYear = yearResponse.data.find(year => year.is_current_year) || yearResponse.data[0];
        setSelectedYear(currentYear.academic_year_id);
        
        // Set first term as default if available
        if (currentYear.terms && currentYear.terms.length > 0) {
          setSelectedTerm(currentYear.terms[0].term_id);
        }
        
        // Fetch classes
        const classResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
        setClasses(classResponse.data);
        
        // Set first class as default if available
        if (classResponse.data.length > 0) {
          setSelectedClass(classResponse.data[0].id);
        }
        
        // Fetch subjects
        const subjectResponse = await api.get('/api/v1/subjects');
        setSubjects(subjectResponse.data);
        
        // Fetch exams
        await fetchExams(currentYear.academic_year_id);
        
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, []);
  
  // Fetch exams when academic year or term changes
  const fetchExams = async (yearId) => {
    try {
      const examResponse = await api.get(`/api/v1/exams/school/${user.school_id}/all-exams`);
      
      // Filter exams by academic year
      const filteredExams = examResponse.data.filter(exam => 
        exam.academic_year_id === yearId && 
        (!selectedTerm || exam.term_id === selectedTerm)
      );
      
      setExams(filteredExams);
      
      // Set first exam as default if available
      if (filteredExams.length > 0) {
        setSelectedExam(filteredExams[0].id);
      } else {
        setSelectedExam(null);
      }
    } catch (error) {
      console.error("Error fetching exams:", error);
    }
  };
  
  // Fetch merit list data
  useEffect(() => {
    const fetchMeritList = async () => {
      if (!selectedClass || !selectedExam) return;
      
      setLoading(true);
      
      try {
        const response = await api.get('/api/v1/reports/merit-list', {
          params: {
            school_id: user.school_id,
            class_id: selectedClass,
            exam_id: selectedExam
          }
        });
        
        setMeritList(response.data);
        setFilteredMeritList(response.data);
        calculatePerformanceMetrics(response.data);
      } catch (error) {
        console.error("Error fetching merit list:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMeritList();
  }, [selectedClass, selectedExam]);
  
  // Update filtered merit list when search or filters change
  useEffect(() => {
    let filtered = [...meritList];
    
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(student => 
        student.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.admission_number.includes(searchQuery)
      );
    }
    
    // Apply subject filter
    if (selectedSubject !== "all") {
      filtered = filtered.filter(student => 
        student.subjects && student.subjects[selectedSubject]
      );
    }
    
    // Apply top ten filter
    if (showOnlyTopTen) {
      filtered = filtered.slice(0, 10);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key === 'class_position') {
        return sortConfig.direction === 'asc' 
          ? a.class_position - b.class_position 
          : b.class_position - a.class_position;
      } else if (sortConfig.key === 'student_name') {
        return sortConfig.direction === 'asc' 
          ? a.student_name.localeCompare(b.student_name) 
          : b.student_name.localeCompare(a.student_name);
      } else if (sortConfig.key === 'total_score') {
        return sortConfig.direction === 'asc' 
          ? a.total_score - b.total_score 
          : b.total_score - a.total_score;
      } else if (sortConfig.key === 'average_score') {
        return sortConfig.direction === 'asc' 
          ? a.average_score - b.average_score 
          : b.average_score - a.average_score;
      } else if (sortConfig.key === 'total_points') {
        return sortConfig.direction === 'asc' 
          ? a.total_points - b.total_points 
          : b.total_points - a.total_points;
      } else if (sortConfig.key.startsWith('subject_')) {
        const subjectId = sortConfig.key.split('_')[1];
        return sortConfig.direction === 'asc' 
          ? (a.subjects[subjectId]?.percentage || 0) - (b.subjects[subjectId]?.percentage || 0)
          : (b.subjects[subjectId]?.percentage || 0) - (a.subjects[subjectId]?.percentage || 0);
      }
      return 0;
    });
    
    setFilteredMeritList(filtered);
  }, [meritList, searchQuery, selectedSubject, sortConfig, showOnlyTopTen]);
  
  // Update exams when year or term changes
  useEffect(() => {
    if (selectedYear) {
      fetchExams(selectedYear);
    }
  }, [selectedYear, selectedTerm]);
  
  // Calculate performance metrics
  const calculatePerformanceMetrics = (data) => {
    if (!data || data.length === 0) {
      setPerformanceMetrics({
        classAverage: 0,
        highestScore: 0,
        lowestScore: 0,
        aboveAverage: 0,
        belowAverage: 0,
      });
      return;
    }
    
    const totalScores = data.map(student => student.average_score);
    const sum = totalScores.reduce((acc, score) => acc + score, 0);
    const avg = parseFloat((sum / data.length).toFixed(2));
    
    const metrics = {
      classAverage: avg,
      highestScore: Math.max(...totalScores),
      lowestScore: Math.min(...totalScores),
      aboveAverage: data.filter(student => student.average_score >= avg).length,
      belowAverage: data.filter(student => student.average_score < avg).length,
    };
    
    setPerformanceMetrics(metrics);
  };
  
  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  
  // Get subject performance statistics
  const getSubjectPerformance = (subjectId) => {
    if (!meritList || meritList.length === 0) return { avg: 0, highest: 0, lowest: 0, pass: 0, fail: 0 };
    
    const studentsWithSubject = meritList.filter(student => 
      student.subjects && student.subjects[subjectId]
    );
    
    if (studentsWithSubject.length === 0) return { avg: 0, highest: 0, lowest: 0, pass: 0, fail: 0 };
    
    const scores = studentsWithSubject.map(student => student.subjects[subjectId].percentage);
    const sum = scores.reduce((acc, score) => acc + score, 0);
    const avg = parseFloat((sum / scores.length).toFixed(2));
    
    return {
      avg,
      highest: Math.max(...scores),
      lowest: Math.min(...scores),
      pass: scores.filter(score => score >= 50).length,
      fail: scores.filter(score => score < 50).length,
    };
  };
  
  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Get selected exam and class info
    const selectedExamObj = exams.find(exam => exam.id === selectedExam);
    const selectedClassObj = classes.find(cls => cls.id === selectedClass);
    
    // Title
    doc.setFillColor(14, 82, 82); // #0E5252
    doc.rect(10, 10, 190, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.text(t('examPerformanceReport'), 105, 22, { align: 'center' });
    
    // Header info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.text(`${t('school')}: ${user.school_name || t('schoolName')}`, 15, 40);
    doc.text(`${t('exam')}: ${selectedExamObj?.exam_name || 'N/A'}`, 15, 48);
    doc.text(`${t('class')}: ${selectedClassObj?.name || 'N/A'}`, 15, 56);
    doc.text(`${t('date')}: ${new Date().toLocaleDateString()}`, 15, 64);
    
    // Performance metrics
    doc.setFillColor(240, 240, 240);
    doc.rect(10, 70, 190, 30, 'F');
    doc.setFontSize(12);
    doc.setTextColor(14, 82, 82);
    doc.text(t('classPerformanceSummary'), 105, 80, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`${t('classAverage')}: ${performanceMetrics.classAverage}%`, 20, 88);
    doc.text(`${t('highestScore')}: ${performanceMetrics.highestScore}%`, 80, 88);
    doc.text(`${t('lowestScore')}: ${performanceMetrics.lowestScore}%`, 140, 88);
    doc.text(`${t('aboveAverage')}: ${performanceMetrics.aboveAverage} ${t('students')}`, 20, 96);
    doc.text(`${t('belowAverage')}: ${performanceMetrics.belowAverage} ${t('students')}`, 80, 96);
    
    // Student results table
    const tableColumns = [
      { header: t('pos'), dataKey: 'position' },
      { header: t('admNo'), dataKey: 'admno' },
      { header: t('name'), dataKey: 'name' },
      { header: t('total'), dataKey: 'total' },
      { header: t('avg'), dataKey: 'avg' },
      { header: t('grade'), dataKey: 'grade' }
    ];
    
    // Add subject columns
    if (meritList.length > 0 && meritList[0].subjects) {
      Object.keys(meritList[0].subjects).forEach(subjectId => {
        const subject = meritList[0].subjects[subjectId];
        tableColumns.push({ 
          header: subject.name.substring(0, 4), 
          dataKey: `subject_${subjectId}` 
        });
      });
    }
    
    // Prepare table data
    const tableData = filteredMeritList.map(student => {
      const data = {
        position: student.class_position,
        admno: student.admission_number,
        name: student.student_name,
        total: student.total_score,
        avg: student.average_score,
        grade: student.overall_grade
      };
      
      // Add subject scores
      if (student.subjects) {
        Object.keys(student.subjects).forEach(subjectId => {
          data[`subject_${subjectId}`] = `${student.subjects[subjectId].percentage}(${student.subjects[subjectId].grade})`;
        });
      }
      
      return data;
    });
    
    // Generate table
    doc.autoTable({
      startY: 105,
      head: [tableColumns.map(col => col.header)],
      body: tableData.map(data => 
        tableColumns.map(col => data[col.dataKey] || '-')
      ),
      theme: 'grid',
      headStyles: { 
        fillColor: [14, 82, 82],
        textColor: [255, 255, 255]
      },
      styles: {
        fontSize: 8,
        cellPadding: 1,
      },
      margin: { top: 105 }
    });
    
    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`${t('page')} ${i} ${t('of')} ${pageCount}`, 105, doc.internal.pageSize.height - 10, { align: 'center' });
    }
    
    // Save the PDF
    doc.save(`Merit_List_${selectedClassObj?.name}_${selectedExamObj?.exam_name}.pdf`);
  };
  
  // Find selected exam name
  const selectedExamName = exams.find(exam => exam.id === selectedExam)?.exam_name || t('loading');
  
  // Render loading state
  if (loading && !meritList.length) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E5252]"></div>
      </div>
    );
  }

  return (
    <div className={`bg-white p-4 rounded-lg shadow ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header Section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('examPerformanceAnalysis')}</h1>
        <p className="text-gray-600">{t('detailedMeritListAndPerformanceInsights')}</p>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Academic Year Select */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('academicYear')}
            </label>
            <select
              className={`w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
              value={selectedYear || ""}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {academicYears.map(year => (
                <option key={year.academic_year_id} value={year.academic_year_id}>
                  {year.year_name} {year.is_current_year ? `(${t('current')})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          {/* Term Select */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('term')}
            </label>
            <select
              className={`w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
              value={selectedTerm || ""}
              onChange={(e) => setSelectedTerm(Number(e.target.value))}
            >
              {selectedYear && academicYears.find(y => y.academic_year_id === selectedYear)?.terms.map(term => (
                <option key={term.term_id} value={term.term_id}>
                  {term.term_name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Class Select */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('class')}
            </label>
            <select
              className={`w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
              value={selectedClass || ""}
              onChange={(e) => setSelectedClass(Number(e.target.value))}
            >
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Exam Select */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('exam')}
            </label>
            <select
              className={`w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
              value={selectedExam || ""}
              onChange={(e) => setSelectedExam(Number(e.target.value))}
            >
              {exams.length > 0 ? (
                exams.map(exam => (
                  <option key={exam.id} value={exam.id}>
                    {exam.exam_name}
                  </option>
                ))
              ) : (
                <option value="">{t('noExamsAvailable')}</option>
              )}
            </select>
          </div>
          
          {/* Subject Filter */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('filterBySubject')}
            </label>
            <select
              className={`w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-teal-500 ${isRTL ? 'text-right' : 'text-left'}`}
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="all">{t('allSubjects')}</option>
              {meritList.length > 0 && meritList[0].subjects && 
                Object.keys(meritList[0].subjects).map(subjectId => (
                  <option key={subjectId} value={subjectId}>
                    {meritList[0].subjects[subjectId].name}
                  </option>
                ))
              }
            </select>
          </div>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className={`flex -mb-px overflow-x-auto scrollbar-hide ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              className={`flex-shrink-0 ${isRTL ? 'ml-4 sm:ml-8' : 'mr-4 sm:mr-8'} py-2 px-1 whitespace-nowrap ${viewMode === 'table' ? 
                'border-b-2 border-teal-700 text-teal-700 font-medium' : 
                'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setViewMode('table')}
            >
              <div className="flex items-center">
                <Users className={`w-4 h-4 ${isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'}`} />
                <span className="text-sm sm:text-base">{t('studentsList')}</span>
              </div>
            </button>
            <button
              className={`flex-shrink-0 ${isRTL ? 'ml-4 sm:ml-8' : 'mr-4 sm:mr-8'} py-2 px-1 whitespace-nowrap ${viewMode === 'subject-analysis' ? 
                'border-b-2 border-teal-700 text-teal-700 font-medium' : 
                'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setViewMode('subject-analysis')}
            >
              <div className="flex items-center">
                <BarChart3 className={`w-4 h-4 ${isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'}`} />
                <span className="text-sm sm:text-base">{t('subjectAnalysis')}</span>
              </div>
            </button>
            <button
              className={`flex-shrink-0 py-2 px-1 whitespace-nowrap ${viewMode === 'top-performers' ? 
                'border-b-2 border-teal-700 text-teal-700 font-medium' : 
                'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setViewMode('top-performers')}
            >
              <div className="flex items-center">
                <Award className={`w-4 h-4 ${isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'}`} />
                <span className="text-sm sm:text-base">{t('topPerformers')}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions and Search */}
      <div className={`flex flex-wrap items-center justify-between mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} mb-4 sm:mb-0`}>
          <button 
            onClick={exportToPDF}
            className="flex items-center bg-teal-700 hover:bg-teal-800 text-white py-2 px-4 rounded-md"
          >
            <FileDown className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('exportPDF')}
          </button>
          <button 
            onClick={() => setShowOnlyTopTen(!showOnlyTopTen)}
            className={`flex items-center py-2 px-4 rounded-md ${
              showOnlyTopTen ? 
              'bg-teal-100 text-teal-800 border border-teal-300' : 
              'bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            <Filter className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            {showOnlyTopTen ? t('showAll') : t('top10Only')}
          </button>
        </div>
        
        <div className="relative">
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('searchByNameOrAdmissionNumber')}
            className={`${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4 text-left'} py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 w-64`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Performance Summary */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <h3 className={`text-lg font-medium text-gray-800 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
          {selectedExamName} {t('performanceSummary')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
            <div className={`text-sm text-gray-500 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('classAverage')}</div>
            <div className={`text-2xl font-bold text-teal-700 ${isRTL ? 'text-right' : 'text-left'}`}>{performanceMetrics.classAverage}%</div>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
            <div className={`text-sm text-gray-500 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('highestScore')}</div>
            <div className={`text-2xl font-bold text-teal-700 ${isRTL ? 'text-right' : 'text-left'}`}>{performanceMetrics.highestScore}%</div>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
            <div className={`text-sm text-gray-500 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('lowestScore')}</div>
            <div className={`text-2xl font-bold text-teal-700 ${isRTL ? 'text-right' : 'text-left'}`}>{performanceMetrics.lowestScore}%</div>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
            <div className={`text-sm text-gray-500 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('aboveAverage')}</div>
            <div className={`text-2xl font-bold text-teal-700 ${isRTL ? 'text-right' : 'text-left'}`}>{performanceMetrics.aboveAverage}</div>
          </div>
          <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
            <div className={`text-sm text-gray-500 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('belowAverage')}</div>
            <div className={`text-2xl font-bold text-teal-700 ${isRTL ? 'text-right' : 'text-left'}`}>{performanceMetrics.belowAverage}</div>
          </div>
        </div>
      </div>

      {/* Main Content based on viewMode */}
      {viewMode === 'table' && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleSort('class_position')}>
                  <div className="flex items-center">
                    {t('position')}
                    {sortConfig.key === 'class_position' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('admNo')}
                </th>
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleSort('student_name')}>
                  <div className="flex items-center">
                    {t('studentName')}
                    {sortConfig.key === 'student_name' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleSort('total_score')}>
                  <div className="flex items-center">
                    {t('totalScore')}
                    {sortConfig.key === 'total_score' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleSort('average_score')}>
                  <div className="flex items-center">
                    {t('averagePercent')}
                    {sortConfig.key === 'average_score' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleSort('total_points')}>
                  <div className="flex items-center">
                    {t('points')}
                    {sortConfig.key === 'total_points' && (
                      sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                    )}
                  </div>
                </th>
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('grade')}
                </th>
                {/* Subject Headers */}
                {filteredMeritList.length > 0 && filteredMeritList[0].subjects && 
                  Object.keys(filteredMeritList[0].subjects).map(subjectId => (
                    <th 
                      key={subjectId}
                      className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleSort(`subject_${subjectId}`)}>
                      <div className="flex items-center whitespace-nowrap">
                        {filteredMeritList[0].subjects[subjectId].name}
                        {sortConfig.key === `subject_${subjectId}` && (
                          sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                        )}
                      </div>
                    </th>
                  ))
                }
                <th className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('remarks')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMeritList.length > 0 ? (
                filteredMeritList.map((student, index) => (
                  <tr key={student.student_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="font-medium">{student.class_position}</span>
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {student.admission_number}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {student.student_name}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {student.total_score}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {student.average_score}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {student.total_points}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        student.overall_grade === 'A' ? 'bg-green-100 text-green-800' :
                        student.overall_grade === 'B' ? 'bg-blue-100 text-blue-800' :
                        student.overall_grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                        student.overall_grade === 'D' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {student.overall_grade}
                      </span>
                    </td>
                    {/* Subject Scores */}
                    {student.subjects && Object.keys(filteredMeritList[0].subjects).map(subjectId => (
                      <td key={subjectId} className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        {student.subjects[subjectId] ? (
                          <div>
                            <div className="font-medium">{student.subjects[subjectId].percentage}% ({student.subjects[subjectId].grade})</div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                    ))}
                    <td className={`px-4 py-3 text-sm text-gray-900 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      {student.overall_remarks}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="100%" className="px-4 py-8 text-center text-gray-500">
                    {t('noDataAvailable')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Subject Analysis View */}
      {viewMode === 'subject-analysis' && (
        <div className="space-y-6">
          <h3 className={`text-lg font-medium text-gray-800 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('subjectPerformanceAnalysis')}
          </h3>
          
          {filteredMeritList.length > 0 && filteredMeritList[0].subjects && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.keys(filteredMeritList[0].subjects).map(subjectId => {
                const subjectPerf = getSubjectPerformance(subjectId);
                const subjectName = filteredMeritList[0].subjects[subjectId].name;
                
                return (
                  <div key={subjectId} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h4 className={`text-lg font-medium text-teal-700 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {subjectName}
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className={`text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('averageScore')}
                        </div>
                        <div className={`text-xl font-bold ${isRTL ? 'text-right' : 'text-left'}`}>
                          {subjectPerf.avg}%
                        </div>
                      </div>
                      <div>
                        <div className={`text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('highestScore')}
                        </div>
                        <div className={`text-xl font-bold ${isRTL ? 'text-right' : 'text-left'}`}>
                          {subjectPerf.highest}%
                        </div>
                      </div>
                      <div>
                        <div className={`text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('lowestScore')}
                        </div>
                        <div className={`text-xl font-bold ${isRTL ? 'text-right' : 'text-left'}`}>
                          {subjectPerf.lowest}%
                        </div>
                      </div>
                      <div>
                        <div className={`text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('passRate')}
                        </div>
                        <div className={`text-xl font-bold ${isRTL ? 'text-right' : 'text-left'}`}>
                          {Math.round((subjectPerf.pass / (subjectPerf.pass + subjectPerf.fail)) * 100) || 0}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Top performers in subject */}
                    <div className="mt-4">
                      <h5 className={`text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('topPerformers')}
                      </h5>
                      <ul className="divide-y divide-gray-200">
                        {filteredMeritList
                          .filter(student => student.subjects && student.subjects[subjectId])
                          .sort((a, b) => b.subjects[subjectId].percentage - a.subjects[subjectId].percentage)
                          .slice(0, 3)
                          .map(student => (
                            <li key={student.student_id} className={`py-2 flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <span className="text-sm">{student.student_name}</span>
                              <span className="text-sm font-medium">{student.subjects[subjectId].percentage}% ({student.subjects[subjectId].grade})</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Top Performers View */}
      {viewMode === 'top-performers' && (
        <div className="space-y-6">
          <h3 className={`text-lg font-medium text-gray-800 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('topPerformersAnalysis')}
          </h3>
          
          {/* Top 5 Overall */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h4 className={`text-lg font-medium text-teal-700 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('top5Overall')}
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('rank')}
                    </th>
                    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('name')}
                    </th>
                    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('admNo')}
                    </th>
                    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('average')}
                    </th>
                    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('grade')}
                    </th>
                    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('remarks')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredMeritList
                    .sort((a, b) => b.average_score - a.average_score)
                    .slice(0, 5)
                    .map((student, index) => (
                      <tr key={student.student_id}>
                        <td className={`px-4 py-3 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className={`flex items-center ${isRTL ? 'justify-end' : 'justify-center'} w-8 h-8 rounded-full bg-teal-700 text-white font-medium`}>
                            {index + 1}
                          </div>
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {student.student_name}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                          {student.admission_number}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {student.average_score}%
                        </td>
                        <td className={`px-4 py-3 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            student.overall_grade === 'A' ? 'bg-green-100 text-green-800' :
                            student.overall_grade === 'B' ? 'bg-blue-100 text-blue-800' :
                            student.overall_grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                            student.overall_grade === 'D' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {student.overall_grade}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                          {student.overall_remarks}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Subject-wise top performers */}
          {filteredMeritList.length > 0 && filteredMeritList[0].subjects && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.keys(filteredMeritList[0].subjects).map(subjectId => {
                const subjectName = filteredMeritList[0].subjects[subjectId].name;
                
                // Get top 3 performers in this subject
                const topPerformers = filteredMeritList
                  .filter(student => student.subjects && student.subjects[subjectId])
                  .sort((a, b) => b.subjects[subjectId].percentage - a.subjects[subjectId].percentage)
                  .slice(0, 3);
                
                return (
                  <div key={subjectId} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h4 className={`text-lg font-medium text-teal-700 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('topIn')} {subjectName}
                    </h4>
                    
                    <div className="space-y-4">
                      {topPerformers.map((student, index) => (
                        <div key={student.student_id} className={`flex items-center space-x-4 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                            index === 0 ? 'bg-yellow-500' :
                            index === 1 ? 'bg-gray-400' :
                            'bg-amber-700'
                          } text-white font-medium`}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                              {student.student_name}
                            </div>
                            <div className={`text-sm text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                              {t('admission')}: {student.admission_number}
                            </div>
                          </div>
                          <div className={`text-right ${isRTL ? 'text-left' : 'text-right'}`}>
                            <div className="text-lg font-bold">{student.subjects[subjectId].percentage}%</div>
                            <div className="text-sm text-gray-500">{t('grade')}: {student.subjects[subjectId].grade}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Most Improved Students */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h4 className={`text-lg font-medium text-teal-700 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('performanceDistribution')}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Grade Distribution */}
              <div className="border border-gray-200 rounded-md p-4">
                <h5 className={`text-sm font-medium text-gray-700 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('gradeDistribution')}
                </h5>
                
                {['A', 'B', 'C', 'D', 'E'].map(grade => {
                  const count = filteredMeritList.filter(student => student.overall_grade === grade).length;
                  const percentage = Math.round((count / filteredMeritList.length) * 100) || 0;
                  
                  return (
                    <div key={grade} className="mb-2">
                      <div className={`flex justify-between text-sm mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span>{t('grade')} {grade}</span>
                        <span>{count} {t('students')} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            grade === 'A' ? 'bg-green-500' :
                            grade === 'B' ? 'bg-blue-500' :
                            grade === 'C' ? 'bg-yellow-500' :
                            grade === 'D' ? 'bg-orange-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Score Range Distribution */}
              <div className="border border-gray-200 rounded-md p-4">
                <h5 className={`text-sm font-medium text-gray-700 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('scoreRangeDistribution')}
                </h5>
                
                {[
                  { range: '80-100', label: t('excellent') },
                  { range: '70-79', label: t('veryGood') },
                  { range: '60-69', label: t('good') },
                  { range: '50-59', label: t('average') },
                  { range: '0-49', label: t('belowAverage') }
                ].map(({ range, label }) => {
                  const [min, max] = range.split('-').map(Number);
                  const count = filteredMeritList.filter(student => 
                    student.average_score >= min && student.average_score <= max
                  ).length;
                  const percentage = Math.round((count / filteredMeritList.length) * 100) || 0;
                  
                  return (
                    <div key={range} className="mb-2">
                      <div className={`flex justify-between text-sm mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span>{range}%</span>
                        <span>{count} {t('students')} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            range === '80-100' ? 'bg-green-500' :
                            range === '70-79' ? 'bg-teal-500' :
                            range === '60-69' ? 'bg-blue-500' :
                            range === '50-59' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Stream Performance */}
              {selectedClass && classes.find(cls => cls.id === selectedClass)?.streams.length > 0 && (
                <div className="border border-gray-200 rounded-md p-4 col-span-2">
                  <h5 className={`text-sm font-medium text-gray-700 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('streamPerformanceComparison')}
                  </h5>
                  
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className={`px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('stream')}
                        </th>
                        <th className={`px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('avgScore')}
                        </th>
                        <th className={`px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('topStudent')}
                        </th>
                        <th className={`px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('topScore')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {classes.find(cls => cls.id === selectedClass)?.streams.map(stream => {
                        // This is a placeholder since we don't have stream data in the merit list API
                        // In a real implementation, you would filter meritList by stream
                        return (
                          <tr key={stream.id}>
                            <td className={`px-2 py-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
                              {stream.name}
                            </td>
                            <td className={`px-2 py-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>-</td>
                            <td className={`px-2 py-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>-</td>
                            <td className={`px-2 py-2 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>-</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {filteredMeritList.length === 0 && (
        <div className="bg-white p-8 text-center rounded-lg border border-gray-200">
          <div className="text-gray-400 mb-4">
            <Users className="w-16 h-16 mx-auto" />
          </div>
          <h3 className={`text-xl font-medium text-gray-800 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('noDataAvailable')}
          </h3>
          <p className={`text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('noRecordsMatchingFilters')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MeritListAnalytics;